import { useQuery, useMutation, UseQueryOptions, UseMutationOptions, useQueryClient } from 'react-query';
import { TeamApi } from './team.api';
import { TeamMemberInDto, TeamOutDto, UserOutDto } from '@app/swagger-types';
import { TeamMemberOutDtoSchema } from './team.schema';
import { PaginatedResponse } from '@app/api/api.type';
import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { useStore } from '@app/store/useStore.hook';

export const useTeamGetByIdQueryKey = 'useTeamGetById';

export const useGetTeamById = (
  id: TeamOutDto['id'],
  options?: UseQueryOptions<unknown, Error, TeamOutDto, [typeof useTeamGetByIdQueryKey, TeamOutDto['id']]>
) => {
  return useQuery([useTeamGetByIdQueryKey, id], () => TeamApi.getTeamById(id), {
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useTeamGetMembersQueryKey = 'useTeamGetMembers';

export const useGetTeamMembers = (
  teamId: TeamOutDto['id'],
  params?: {
    statuses?: string[];
    roles?: string[];
    term?: string;
    fields?: string;
    page?: number;
    size?: number;
    sort?: string;
  },
  options?: UseQueryOptions<
    unknown,
    Error,
    PaginatedResponse<TeamMemberOutDtoSchema>,
    [typeof useTeamGetMembersQueryKey, TeamOutDto['id'], typeof params]
  >
) => {
  return useQuery(
    [useTeamGetMembersQueryKey, teamId, params],
    () => {
      return TeamApi.getTeamMembers(teamId, params);
    },
    {
      enabled: !!teamId,
      keepPreviousData: true,
      staleTime: 0,
      ...options,
    }
  );
};

export const useInviteTeamMembers = (options?: UseMutationOptions<void, Error, TeamMemberInDto>) => {
  const queryClient = useQueryClient();
  const getMe = useStore.useGetMe();
  const { currentUser: user } = useAuthorizedUser();

  return useMutation((body) => TeamApi.inviteTeamMembers(body), {
    onSuccess: async (_, variables) => {
      await getMe();
      queryClient.invalidateQueries([useTeamGetMembersQueryKey, user?.teamId]);
      queryClient.invalidateQueries([useTeamGetByIdQueryKey, user?.teamId]);

      if (options?.onSuccess) {
        options.onSuccess(_, variables, useTeamGetMembersQueryKey);
      }
    },
    ...options,
  });
};

export const useDeleteTeamMember = (
  options?: UseMutationOptions<
    void,
    Error,
    { teamId: NonNullable<UserOutDto['teamId']>; teamMemberId: TeamOutDto['id'] }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation(({ teamId, teamMemberId }) => TeamApi.deleteTeamMember(teamId, teamMemberId), {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([useTeamGetMembersQueryKey, variables.teamId]);
      queryClient.refetchQueries([useTeamGetMembersQueryKey, variables.teamId]);
      queryClient.invalidateQueries([useTeamGetByIdQueryKey, variables.teamId]);

      if (options?.onSuccess) {
        options.onSuccess(_, variables, undefined);
      }
    },
    ...options,
  });
};
